import { useMemo, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { AttorneyLeadStatus, AttorneyLeadsResponse } from "../../../../api";
import { LeadType, StageColorsMapping } from "../../LeadsManagement/shared/Constants";
import { HelpersService } from "../../../../services/Helpers";
import useNavigateSearch from "../../../../services/useNavigateSearch";
import "../../LeadsManagement/Leads.scss";

const DEFAULT_ROWS_PER_PAGE = 10;

const STAGE_FILTERS: {
  status: AttorneyLeadStatus | null;
  label: string;
}[] = [
  { status: null, label: "All" },
  { status: AttorneyLeadStatus.NEW, label: "New" },
  { status: AttorneyLeadStatus.ACCEPTED, label: "Accepted" },
  { status: AttorneyLeadStatus.REJECTED, label: "Rejected" },
  { status: AttorneyLeadStatus.LOST, label: "Opportunities lost" },
  { status: AttorneyLeadStatus.WON, label: "Opportunities won" },
  { status: AttorneyLeadStatus.SIGNED, label: "Signed" },
  { status: AttorneyLeadStatus.PAID, label: "Paid" },
];

type Props = {
  attorneyLeads?: Record<string, AttorneyLeadsResponse[] | null> | null;
};

type CaseRow = AttorneyLeadsResponse & {
  leadName: string;
};

function flattenAttorneyLeads(
  attorneyLeads?: Record<string, AttorneyLeadsResponse[] | null> | null
): CaseRow[] {
  if (!attorneyLeads) {
    return [];
  }

  return Object.values(attorneyLeads)
    .flatMap((leads) => leads ?? [])
    .map((lead) => ({
      ...lead,
      leadName: `${lead.firstName ?? ""} ${lead.lastName ?? ""}`.trim(),
    }))
    .sort((a, b) => a.leadName.localeCompare(b.leadName));
}

export default function AttorneyCasesTable({ attorneyLeads }: Props) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStage, setSelectedStage] = useState<AttorneyLeadStatus | null>(
    null
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const navigate = useNavigateSearch();

  const allCases = useMemo(
    () => flattenAttorneyLeads(attorneyLeads),
    [attorneyLeads]
  );

  const countByStatus = useMemo(() => {
    const counts: Partial<Record<AttorneyLeadStatus, number>> = {};
    allCases.forEach((row) => {
      if (row.status) {
        counts[row.status] = (counts[row.status] ?? 0) + 1;
      }
    });
    return counts;
  }, [allCases]);

  const stageFilteredCases = useMemo(() => {
    if (!selectedStage) {
      return allCases;
    }
    return allCases.filter((row) => row.status === selectedStage);
  }, [allCases, selectedStage]);

  const filteredCases = useMemo(() => {
    if (!searchTerm.trim()) {
      return stageFilteredCases;
    }

    const lower = searchTerm.toLowerCase();
    return stageFilteredCases.filter((row) =>
      row.leadName.toLowerCase().includes(lower)
    );
  }, [stageFilteredCases, searchTerm]);

  const paginatedCases = filteredCases.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleStageChange = (status: AttorneyLeadStatus | null) => {
    setSelectedStage(status);
    setPage(0);
  };

  const handleOpenLead = (leadId?: number) => {
    if (!leadId) {
      return;
    }
    navigate(`/lead/${leadId}`, { leadType: LeadType.Admin, isEdit: "false" });
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box className="d-flex table-filters flex-wrap align-items-center mb-3">
        {STAGE_FILTERS.map(({ status, label }) => {
          const count =
            status === null ? allCases.length : countByStatus[status] ?? 0;
          const isSelected = selectedStage === status;

          if (status === null) {
            return (
              <Button
                key="all"
                variant="contained"
                color={isSelected ? "primary" : "darkGray"}
                size="small"
                onClick={() => handleStageChange(null)}
              >
                {label}({count})
              </Button>
            );
          }

          return (
            <Button
              key={status}
              variant="contained"
              color={StageColorsMapping[status]}
              size="small"
              sx={
                isSelected
                  ? { boxShadow: 3, outline: "2px solid #393939" }
                  : undefined
              }
              onClick={() => handleStageChange(status)}
            >
              {label}({count})
            </Button>
          );
        })}
      </Box>

      <TextField
        size="small"
        placeholder="Search cases..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setPage(0);
        }}
        sx={{ width: { xs: "100%", sm: 280 }, mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {selectedStage === null && (
                <TableCell sx={{ fontWeight: 600, width: 200 }}>Stage</TableCell>
              )}
              <TableCell sx={{ fontWeight: 600 }}>Lead name</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedCases.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={selectedStage === null ? 2 : 1}
                  align="center"
                  sx={{ py: 4, color: "text.secondary" }}
                >
                  {allCases.length === 0
                    ? "No cases found."
                    : "No cases match the selected filters."}
                </TableCell>
              </TableRow>
            ) : (
              paginatedCases.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => handleOpenLead(row.id)}
                  sx={{ cursor: "pointer" }}
                >
                  {selectedStage === null && (
                    <TableCell>
                      <Button
                        variant="contained"
                        color={StageColorsMapping[row.status!]}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {HelpersService.camelToString(row.status)}
                      </Button>
                    </TableCell>
                  )}
                  <TableCell>{row.leadName || "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredCases.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </Box>
  );
}
